/**
 * ParentConsentScreen — Shown after a parent completes OTP sign-in for an
 * under-13 child. Captures:
 *   - Child's first name (nickname only — no last name, no school, no DOB)
 *   - Parental consent affirmation (FTC's "email plus" pattern for COPPA)
 *
 * On confirm, writes to the Supabase user's `user_metadata`:
 *   - role: "parent"
 *   - kid_nickname: "<first name>"
 *   - parental_consent_at: ISO timestamp
 *
 * Once stored, the kid plays under the parent's user_id. We never collect
 * the kid's email, real name, address, school, or birth date.
 */

import { useState } from "react";
import { supabase } from "../lib/supabase";

interface Props {
  parentEmail: string;
  onComplete: () => void;
}

export default function ParentConsentScreen({ parentEmail, onComplete }: Props) {
  const [kidName, setKidName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    const cleaned = kidName.trim();
    if (!cleaned) { setError("Please enter your child's first name (nickname is fine)."); return; }
    if (cleaned.length > 24) { setError("First name must be 24 characters or less."); return; }
    if (!confirmed) { setError("Please confirm you're the parent or legal guardian."); return; }
    if (!supabase) { setError("Sign-up temporarily unavailable."); return; }

    setBusy(true);
    try {
      const { error: err } = await supabase.auth.updateUser({
        data: {
          role: "parent",
          kid_nickname: cleaned,
          parental_consent_at: new Date().toISOString(),
          parental_consent_email: parentEmail,
        },
      });
      if (err) {
        setError(err.message || "Couldn't save consent. Try again.");
      } else {
        onComplete();
      }
    } catch (e: any) {
      setError(e?.message || "Network error. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={brandStyle}>MoneyMission</div>

        <h1 style={titleStyle}>Set up your child's account</h1>

        <p style={subtitleStyle}>
          Almost there. We just need a name to display in the app — and your confirmation that you're a parent or legal guardian.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="kidName" style={labelStyle}>Your child's first name or nickname</label>
          <input
            id="kidName" name="kidName" type="text" autoComplete="off"
            value={kidName}
            onChange={e => setKidName(e.target.value)}
            placeholder="e.g. Sam"
            maxLength={24}
            required autoFocus
            style={inputStyle}
          />
          <p style={inputHintStyle}>
            We only use this in the app — never shared, never linked to any other info. A nickname is fine.
          </p>

          <label style={consentBoxStyle}>
            <input
              type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
              style={{ marginRight: 12, width: 20, height: 20, accentColor: "#0d9488", flexShrink: 0 }}
            />
            <span style={consentTextStyle}>
              I confirm I am the parent or legal guardian of this child, and I consent
              to MoneyMission collecting only the information needed to provide the
              service (this nickname and my email). I understand I can request deletion
              at any time.
            </span>
          </label>

          {error && <div style={errorStyle}>{error}</div>}

          <button
            type="submit" disabled={busy || !kidName.trim() || !confirmed}
            style={primaryButtonStyle(busy || !kidName.trim() || !confirmed)}
          >
            {busy ? "Saving…" : "Start playing"}
          </button>
        </form>

        <p style={fineprintStyle}>
          What we collect: parent email + child's nickname. What we don't collect:
          child's email, real name, address, school, birth date, or location.
        </p>
      </div>
    </div>
  );
}

/* ─ Styles ───────────────────────────────────────────────────────────────── */
const containerStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #042f2e 0%, #134e4a 60%, #0d9488 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  paddingTop: "max(20px, env(safe-area-inset-top, 0px))",
  paddingBottom: "max(20px, env(safe-area-inset-bottom, 0px))",
  fontFamily: "Nunito, system-ui, sans-serif",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 28,
  padding: "26px 22px",
  maxWidth: 460,
  width: "100%",
  boxShadow: "0 30px 80px rgba(0, 0, 0, 0.30)",
};

const brandStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 900,
  color: "#0d9488",
  fontFamily: "Poppins, system-ui, sans-serif",
  textAlign: "center",
  marginBottom: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 900,
  color: "#0f172a",
  margin: "4px 0 8px",
  textAlign: "center",
  fontFamily: "Fredoka One, system-ui, sans-serif",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#475569",
  margin: "0 0 22px",
  lineHeight: 1.5,
  textAlign: "center",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 6,
  marginTop: 4,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  fontSize: 16,
  border: "2px solid #99f6e4",
  borderRadius: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Nunito, system-ui, sans-serif",
  fontWeight: 600,
  color: "#1f2937",
};

const inputHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#94a3b8",
  marginTop: 6,
  marginBottom: 18,
  lineHeight: 1.5,
};

const consentBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  background: "#f0fdfa",
  border: "2px solid #99f6e4",
  borderRadius: 14,
  padding: "14px 16px",
  cursor: "pointer",
  marginBottom: 12,
};

const consentTextStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "#134e4a",
  lineHeight: 1.5,
};

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: "10px 14px",
  background: "#fee2e2",
  color: "#991b1b",
  borderRadius: 12,
  fontSize: 13,
  fontWeight: 600,
  borderLeft: "4px solid #ef4444",
};

const fineprintStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#94a3b8",
  marginTop: 18,
  lineHeight: 1.6,
  textAlign: "center",
};

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: 15,
    fontSize: 16,
    fontWeight: 800,
    background: disabled ? "#d1d5db" : "linear-gradient(135deg, #0d9488, #f59e0b)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    cursor: disabled ? "not-allowed" : "pointer",
    marginTop: 14,
    fontFamily: "Nunito, system-ui, sans-serif",
    minHeight: 50,
  };
}
